import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SplashCursor from '../components/SplashCursor';
import backgroundImage from '../assets/images/c-room.jpg';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInOnScroll = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: transparent;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  scroll-behavior: smooth;

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.3);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 5px;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
  }

  /* Smooth Scroll Effect */
  & {
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    overflow-y: overlay;
  }

  /* Add this to the root element */
  @media screen and (prefers-reduced-motion: no-preference) {
    html {
      scroll-behavior: smooth;
    }
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  padding: 0 5%;
  pointer-events: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.8s ease-out;
  scroll-behavior: smooth;
`;

const HeroSection = styled.div`
  padding-top: 120px;
  text-align: center;
  max-width: 900px;
  margin: 0 auto 4rem;
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.2;
  font-weight: bold;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.79);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 3rem;
  line-height: 1.6;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ManifestoSection = styled.div`
  max-width: 800px;
  margin: 0 auto 6rem;
`;

const ManifestoItem = styled.div`
  margin-bottom: 4rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ItemTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: white;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ItemContent = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
  
  p {
    margin-bottom: 1.5rem;
  }
  
  strong {
    color: #3B82F6;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Highlight = styled.span`
  color: #3B82F6;
  font-weight: 600;
`;

function Manifesto() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <SplashCursor />
      <Navbar />
      <ContentContainer>
        <HeroSection>
          <Title>
            The GeoSpatios Manifesto
          </Title>
          <Subtitle>
            Building the ecosystem for autonomous operations
          </Subtitle>
        </HeroSection>

        <ManifestoSection>
          <ManifestoItem>
            <ItemTitle>
              The Operational Gap
            </ItemTitle>
            <ItemContent>
              <p>
                In today's world, <strong>high-stakes operations remain fragmented</strong>. While boardrooms leverage advanced analytics and real-time data, frontline operators struggle with piecing together disconnected systems, manual processes, and point solutions that create more complexity than clarity.
              </p>
              <p>
                This fragmentation creates a dangerous gap between strategic vision and operational reality. When critical decisions require human operators to process multiple data streams manually, the result is inevitable: <strong>delayed decisions, missed opportunities, and increased operational risk</strong>.
              </p>
            </ItemContent>
          </ManifestoItem>

          <ManifestoItem>
            <ItemTitle>
              Beyond Human-in-the-Loop
            </ItemTitle>
            <ItemContent>
              <p>
                The future of operations demands more than just automations. It requires <strong>intelligent orchestration at machine speed</strong>. Traditional approaches that rely solely on human processing create bottlenecks in operational tempo and effectiveness. Our approach transforms this paradigm by enabling systems to make decisions at digital speed while maintaining human oversight at the strategic level.
              </p>
              <p>
              Our goal is to <strong>multiply human capability</strong> rather than simply automate existing workflows. By unifying fragmented systems into one command center, we enable operators to focus on strategic decisions while our digital operators handle the complexity of real-time coordination and execution.
              </p>
            </ItemContent>
          </ManifestoItem>

          <ManifestoItem>
            <ItemTitle>
              The Digital Factory
            </ItemTitle>
            <ItemContent>
              <p>
                GeoSpatios is building <strong>Geo-OS</strong>, our digital factory for creating operational intelligence. This unified Operating System combines geospatial data processing, foundation models, and modular functions into a powerful infrastructure that enables rapid development of specialized digital operators at planetary scale.
              </p>
              <p>
                Through Geo-OS's modular architecture, we can quickly assemble and deploy <strong>digital operators</strong> tailored to specific operational domains. Each digital operator inherits core capabilities while being customized for its unique operational context—whether that's air traffic control, maritime dispatch, or emergency response.
              </p>
            </ItemContent>
          </ManifestoItem>

          <ManifestoItem>
            <ItemTitle>
              Digital Operators in Action
            </ItemTitle>
            <ItemContent>
              <p>
                Our digital operators transform fragmented operations into unified command & control. Each operator bridges the gap between strategic, tactical, and operational layers—processing millions of data points to enable <strong>confident decision-making at machine speed</strong>.
              </p>
              <p>
                From coordinating thousands of flights to optimizing global maritime routes, from emergency response to space operations, these digital operators work 24/7 to <strong>orchestrate complex missions with unprecedented speed and precision</strong>. What was once impossible becomes standard operating procedure.
              </p>
            </ItemContent>
          </ManifestoItem>

          <ManifestoItem>
            <ItemTitle>
              Our Mission
            </ItemTitle>
            <ItemContent>
              <p>
                GeoSpatios exists to change how humans handle high-stakes operations. We're creating the infrastructure that enables humans to safely operate at global scale, automating the routine so that people can focus on the exceptional.
              </p>
            </ItemContent>
          </ManifestoItem>
        </ManifestoSection>

        <Footer />
      </ContentContainer>
    </PageContainer>
  );
}

export default Manifesto; 